import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Paper } from "@mantine/core";

import { GET_CONTESTS_BY_NAME, ADD_PARTICIPANT_TO_CONTEST, GET_PARTICIPANTS, REMOVE_PARTICIPANT_FROM_CONTEST } from "../../../queries/contest";

import "./ContestInfoCard.css";

function ContestInfoCard({ userid, contestname, refetchLeaderboard }) {
  const [hasParticipated, setHasParticipated] = useState(false);
  const navigate = useNavigate();
  const tradePath = `/trade`;

  const { data: contestQueryData } = useQuery(GET_CONTESTS_BY_NAME, {
    variables: { contestname: contestname },
  });

  const {
    loading: participantLoading,
    error: participantError,
    data: participantData,
    refetch: refetchParticipants,
  } = useQuery(GET_PARTICIPANTS, {
    variables: { contestname: contestname, userid: userid },
  });

  useEffect(() => {
    if (participantLoading) { console.log("loading participant data"); }
    else if (participantError) { console.log(participantError); }
    else { console.log("PARTICIPANT DATA: " + participantData.getParticipants); participantData.getParticipants === null ? setHasParticipated(false) : setHasParticipated(true) }
  }, [participantLoading, participantError, participantData, hasParticipated])

  const [addParticipantToContest] = useMutation(ADD_PARTICIPANT_TO_CONTEST, {
    refetchQueries: ['IsUserInOngoingContest', 'GetUserBalance'],
    awaitRefetchQueries: true,
  });
  const [removeParticipant] = useMutation(REMOVE_PARTICIPANT_FROM_CONTEST, {
    refetchQueries: ['IsUserInOngoingContest', 'GetUserBalance'],
    awaitRefetchQueries: true,
  });

  const handleJoinClick = async () => {
    try {
      console.log('Attempting to join contest:', contestname, 'for user:', userid);
      const { data } = await addParticipantToContest({
        variables: {
          contestname: contestname
        },
      });
      
      console.log('Join contest response:', data.addParticipantToContest);
      
      // Refetch all relevant data after joining
      refetchParticipants();
      refetchLeaderboard();
      
      // The refetchQueries in the mutation will handle updating the balance and contest status
      console.log('Successfully joined contest and refreshed data');
  
    } catch (error) {
      console.error("Error joining contest:", error);
      alert('Error joining contest: ' + error.message);
    }
  };

  const handleLeaveClick = async () => {
    try {
      console.log('Attempting to leave contest:', contestname, 'for user:', userid);
      const { data } = await removeParticipant({
        variables: {
          contestname: contestname
        },
      });
      
      console.log('Leave contest response:', data.removeParticipantFromContest);
      
      // Refetch all relevant data after leaving
      refetchParticipants();
      refetchLeaderboard();
      
      // The refetchQueries in the mutation will handle updating the balance and contest status
      console.log('Successfully left contest and refreshed data');
  
    } catch (error) {
      console.error("Error leaving contest:", error);
      alert('Error leaving contest: ' + error.message);
    }
  };

  // Derive status directly from query data — no intermediate state needed.
  // When the query is loading, contestInfo is undefined and status is "Loading",
  // which keeps the button disabled until we have real data.
  const contestInfo = contestQueryData?.getContestByName ?? null;
  const status = (() => {
    if (!contestInfo) return "Loading";
    const now = Date.now();
    // Timestamps arrive as epoch-ms strings; new Date(string) returns NaN for those.
    const end = parseInt(contestInfo.endat);
    const start = parseInt(contestInfo.startat);
    if (end < now) return "Ended";
    if (start > now) return "Upcoming";
    return "In Progress";
  })();

  // Convert timestamp to human-readable format
  const formatDate = (timestamp) => {
    const numericTimestamp = parseInt(timestamp); // Convert to a number
    if (isNaN(numericTimestamp)) {
      return "Invalid Date"; // Return this if the timestamp is not valid
    }

    const date = new Date(numericTimestamp);
    return date.toLocaleString(); // Convert to human-readable format
  };

  return (
    <Paper
      shadow="sm"
      radius="md"
      withBorder
      pt="md"
      pb="xs"
      mx="sm"
      className="card"
      style={{ minHeight: 'auto' }}
    >
      <div className="container">
        <h3 className="title">Contest Details</h3>
        <div className="content">
          <p>
            <b>Name:</b> {contestInfo?.name ?? "—"}
          </p>
          <p>
            <b>Status:</b> {status}
          </p>
          <p>
            <b>Start:</b> {contestInfo ? formatDate(contestInfo.startat) : "—"}
          </p>
          <p>
            <b>End:</b> {contestInfo ? formatDate(contestInfo.endat) : "—"}
          </p>
        </div>
        <div className="button-container">
          <Button
              className="join-button"
              color={status === "Ended" ? "gray" : hasParticipated ? "red" : "green"}
              size="md"
              onClick={hasParticipated ? handleLeaveClick : handleJoinClick}
              disabled={status === "Ended" || status === "Loading"}
            >
            {status === "Ended" ? "Contest Ended" : hasParticipated ? "Leave" : "Join"}
          </Button>
          <Button className="trade-button" color="blue" size="md" disabled={!hasParticipated} onClick={() => navigate(tradePath)}>
            Trade
          </Button>
        </div>
      </div>
    </Paper>
  );
}


export default ContestInfoCard;
